import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toErrorMessage } from "../lib/errors";
import { Check } from "lucide-react";

// Project type dropdown aligns with ?type= URL parameter keys.
const projectTypes = [
  { label: "Custom Home", value: "custom-home" },
  { label: "Whole-Home Renovation or Addition", value: "renovation" },
  { label: "Kitchen or Bath Remodel", value: "kitchen-bath" },
  { label: "Commercial Construction", value: "commercial" },
  { label: "Disaster Recovery / FEMA PA", value: "disaster-recovery" },
  { label: "Federal Construction or Teaming", value: "federal" },
  { label: "Expert Witness", value: "expert-witness" },
  { label: "Architect or Designer", value: "designer" },
  { label: "Subcontractor or Vendor", value: "trade-partner" },
  { label: "Careers", value: "careers" },
  { label: "Other", value: "other" },
];

const budgetRanges = [
  "Under $50K", "$50K to $150K", "$150K to $500K",
  "$500K to $1M", "$1M+", "Not sure yet",
];

const referralSources = [
  "Google search",
  "AI assistant (ChatGPT, Claude, Gemini, etc.)",
  "Referral from a friend or neighbor",
  "Referral from an architect or designer",
  "Social media",
  "Job site sign",
  "Other",
];

const DEFAULT_TYPE = projectTypes[0].value;

export default function ContactForm({ dark = false }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    project_type: DEFAULT_TYPE,
    budget: budgetRanges[5],
    referral_source: referralSources[0],
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const location = useLocation();

  // Pre-select Project Type from ?type= URL parameter.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("type");
    if (t && projectTypes.some((pt) => pt.value === t)) {
      setForm((f) => ({ ...f, project_type: t }));
    }
  }, [location.search]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const [first, ...rest] = form.name.trim().split(/\s+/);
      const labelFor = projectTypes.find((pt) => pt.value === form.project_type)?.label || form.project_type;
      await api.post("/contact", {
        first_name: first || form.name,
        last_name: rest.join(" "),
        email: form.email,
        phone: form.phone,
        project_type: labelFor,
        budget: form.budget,
        referral_source: form.referral_source,
        message: form.message,
      });
      setStatus("success");
      setForm({ name: "", phone: "", email: "", project_type: DEFAULT_TYPE, budget: budgetRanges[5], referral_source: referralSources[0], message: "" });
    } catch (err) {
      setStatus("error");
      setError(toErrorMessage(err));
    }
  };

  const txt = dark ? "text-cream-light" : "text-ink";
  const border = dark ? "border-cream-light/25 focus:border-copper" : "border-ink/20 focus:border-copper";
  const input = `w-full bg-transparent border-b ${border} py-4 px-0 ${txt} outline-none transition-colors`;
  const label = `text-[10px] tracking-[0.22em] uppercase ${dark ? "text-cream-light/60" : "text-ink/60"} mb-1 block`;

  if (status === "success") {
    return (
      <div data-testid="contact-success" className={`${txt} py-14 text-center`}>
        <div className="w-14 h-14 rounded-full border-2 border-copper mx-auto flex items-center justify-center mb-5">
          <Check className="text-copper" size={28} />
        </div>
        <h3 className="font-serif text-3xl mb-3">Thanks.</h3>
        <p className={dark ? "text-cream-light/70" : "text-ink/70"}>
          We received your message and will respond within one business day.
        </p>
        <button onClick={() => setStatus("idle")} className={`mt-8 text-[11px] tracking-[0.18em] uppercase ${dark ? "text-copper-light" : "text-copper"} link-underline`}>
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-7">
      <div>
        <label className={label}>Name</label>
        <input data-testid="contact-name" name="name" value={form.name} onChange={onChange} required className={input} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        <div>
          <label className={label}>Phone</label>
          <input data-testid="contact-phone" name="phone" type="tel" inputMode="tel" value={form.phone} onChange={onChange} required className={input} />
        </div>
        <div>
          <label className={label}>Email</label>
          <input data-testid="contact-email" name="email" type="email" value={form.email} onChange={onChange} required className={input} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        <div>
          <label className={label}>Project Type</label>
          <select data-testid="contact-project-type" name="project_type" value={form.project_type} onChange={onChange} className={`${input} cursor-pointer`}>
            {projectTypes.map((t) => <option key={t.value} value={t.value} className="bg-cream-light text-ink">{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Budget</label>
          <select data-testid="contact-budget" name="budget" value={form.budget} onChange={onChange} className={`${input} cursor-pointer`}>
            {budgetRanges.map((b) => <option key={b} value={b} className="bg-cream-light text-ink">{b}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={label}>Tell us about your project</label>
        <textarea data-testid="contact-message" name="message" value={form.message} onChange={onChange} required rows={4} className={`${input} resize-none`} />
      </div>
      <div>
        <label className={label}>How did you hear about us?</label>
        <select data-testid="contact-referral-source" name="referral_source" value={form.referral_source} onChange={onChange} className={`${input} cursor-pointer`}>
          {referralSources.map((s) => <option key={s} value={s} className="bg-cream-light text-ink">{s}</option>)}
        </select>
      </div>
      {error && <div className="text-red-500 text-sm" data-testid="contact-error">{error}</div>}
      <button data-testid="contact-submit" type="submit" disabled={status === "loading"} className="btn-copper disabled:opacity-50">
        {status === "loading" ? "Sending..." : "Send Message →"}
      </button>
      <p className={`text-xs ${dark ? "text-cream-light/55" : "text-ink/55"} mt-3`}>
        By submitting this form you agree to our <Link to="/privacy" className="underline hover:text-copper">Privacy Policy</Link>.
      </p>
    </form>
  );
}
